import { useCallback, useEffect, useRef, useState } from 'react';
import { translatePageText, updatePageText } from '../api/pageApi';

const LANGUAGE_CODES = ['de', 'en', 'es', 'nl', 'ru', 'it', 'fr', 'tr', 'pl'];

const emptyTextByLang = () => LANGUAGE_CODES.reduce((texts, language) => {
  texts[language] = '';
  return texts;
}, {});

const textValuesFromPage = (pageData) => ({
  en: pageData.text_en || '',
  es: pageData.text_es || '',
  de: pageData.text_de || '',
  nl: pageData.text_nl || '',
  ru: pageData.text_ru || '',
  it: pageData.text_it || '',
  fr: pageData.text_fr || '',
  tr: pageData.text_tr || '',
  pl: pageData.text_pl || '',
});

export default function usePageTranslations({ pageId, pageData, notify }) {
  const [textByLang, setTextByLang] = useState(emptyTextByLang);
  const [savingFields, setSavingFields] = useState({});

  const loadedPageId = useRef(null);
  useEffect(() => {
    if (!pageData || loadedPageId.current === pageData._id) return;
    loadedPageId.current = pageData._id;
    setTextByLang(textValuesFromPage(pageData));
  }, [pageData]);

  const getCurrentTextValues = useCallback(() => {
    const values = { ...textByLang };

    // Keep the editor's current DOM value authoritative while an input update
    // is waiting to commit to React state.
    if (typeof document !== 'undefined') {
      LANGUAGE_CODES.forEach((code) => {
        const element = document.querySelector(`[data-testid="language-text-${code}"]`);
        if (element && typeof element.value === 'string') {
          values[code] = element.value;
        }
      });
    }

    return values;
  }, [textByLang]);

  const translateText = useCallback(async (targetLang) => {
    if (!pageId) {
      alert('Page ID is missing.');
      return;
    }

    // Use the current English text from the input, falling back to the API's
    // stored value when the field is empty.
    const textToTranslate = textByLang.en.trim() ? textByLang.en : null;

    try {
      const response = await translatePageText(pageId, targetLang, textToTranslate);

      if (response.status === 200) {
        const translatedText = response.data.translated_text;
        if (targetLang === 'es' || targetLang === 'de' || targetLang === 'nl' ||
          targetLang === 'ru' || targetLang === 'it' || targetLang === 'fr' ||
          targetLang === 'tr' || targetLang === 'pl') {
          setTextByLang((current) => ({ ...current, [targetLang]: translatedText }));
        }
      } else {
        alert('Translation failed. Please try again.');
      }
    } catch (error) {
      console.error('Error translating text:', error);
      alert('Error translating text. Please check the console.');
    }
  }, [pageId, textByLang.en]);

  const saveTextChanges = useCallback(async (field, value) => {
    if (!pageId || !value) {
      alert('Page ID or text is missing.');
      return;
    }

    setSavingFields((previous) => ({ ...previous, [field]: true }));

    try {
      const response = await updatePageText(pageId, field, value);

      if (response.status === 200) {
        notify('Text updated!');
      } else {
        notify('Content already exists', 'error');
      }
    } catch (error) {
      notify('Error updating text', 'error');
    } finally {
      setSavingFields((previous) => ({ ...previous, [field]: false }));
    }
  }, [notify, pageId]);

  return {
    textByLang,
    setTextByLang,
    savingFields,
    getCurrentTextValues,
    translateText,
    saveTextChanges,
  };
}
